import React, {useEffect, useMemo, useRef, useState} from 'react';
import MainLayout from '../../mainLayout';
import styled, {useTheme} from 'styled-components/native';
import {Theme} from '../../../theme/lightTheme';
import {getFontFamily, pickTabsByKey} from '../../../utils/helpers';
import {RootState} from '../../../redux/store';
import {useSelector} from 'react-redux';
import {HomeTabItemType} from '../../../interfaces/reducers/homeState';
import {tr} from '../../../translations';
import useResponsiveDimensions from '../../../utils/hooks/useResponsiveDimensions';
import {FilterTabNewsProps} from '../../../interfaces/screens/filterTabNews';
import {FlatList} from 'react-native-gesture-handler';
import {
  ViewToken,
  Dimensions,
  ListRenderItem,
  Platform,
  BackHandler,
} from 'react-native';
import {TabKey} from '../../../utils/enums';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  useAnimatedScrollHandler,
  useDerivedValue,
  runOnJS,
  FadeInDown,
  FadeOutDown,
} from 'react-native-reanimated';
import GenericButton from '../../../components/button';
import TextGeneric from '../../../components/textGeneric';
import {useCustomHeaderHeight} from '../../../utils/hooks/useHeaderHeight';
import DrugsDiseasesTab from './drugsDiseasesTab';
import TextInput from '../../../components/textInput';
import {LinearGradient} from 'react-native-linear-gradient';
import DrugsDiseasesSearch from './drugsDiseasesSearch';
import {isWeb} from '../../../utils/responsive';

const Container = styled.View`
  flex: 1;
  width: 100%;
`;

const TabContainer = styled.View<{isHidden?: boolean}>`
  width: 100%;
  justify-content: center;
  align-items: center;
  left: 0;
  z-index: 2;
  border-bottom-right-radius: 30px;
  border-bottom-left-radius: 30px;
  overflow: hidden;

  ${({isHidden}) =>
    isHidden
      ? `
      pointer-events:none;
      opacity:0;
      `
      : `
  position: absolute;
  top:0;
  left: 0;
  `}
`;

const TabContentContainer = styled.View<{tabContainerWidth: string}>`
  width: ${({tabContainerWidth}) => tabContainerWidth};
  justify-content: space-evenly;
  align-items: center;
  flex-direction: row;
  border-width: 1px;
  border-color: ${({theme}) => theme.text.light};
  border-radius: 100px;
  padding: ${({theme}) => theme.text.s12 / 2}px;
  position: relative;
`;

const AnimatedSlider = styled(Animated.View)`
  position: absolute;
  height: ${isWeb ? 90 : 100}%;
  width: 50%;
  background-color: ${({theme}) => theme.drugsDiseases.tabBackground};
  border-radius: 100px;
`;

const Title = styled(TextGeneric)`
  color: ${({theme}) => theme.text.light};
  width: 90%;
  font-size: ${({theme}) => theme.text.s55}px;
  margin-bottom: 4%;
  text-align: left;
`;

const SearchInputContainer = styled.View<{
  tabMarginVertical: number;
  tabContainerWidth: number;
}>`
  width: ${({tabContainerWidth}) => tabContainerWidth};
  margin-bottom: ${({tabMarginVertical}) => tabMarginVertical}px;
  margin-top: ${({tabMarginVertical}) => tabMarginVertical}px;
`;

const SearchInput = styled(TextInput)<{minHeight: number}>`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  min-height: ${({minHeight}) => minHeight}px;
  color: ${({theme}) => theme.text.light};
`;

const ItemContainer = styled(Animated.View)<{
  screenWidth: number;
}>`
  width: ${({screenWidth}) => screenWidth}px;
`;

const LIST: TabKey[] = [TabKey.Drugs, TabKey.Diseases];
const SPRING_CONFIG = {
  damping: 20,
  stiffness: 90,
  mass: 1,
  overshootClamping: false,
  restDisplacementThreshold: 0.01,
  restSpeedThreshold: 0.01,
};

/**
 * FilterTabNews screen component with enhanced animations.
 *
 * @param {FilterTabNewsProps} props - The screen component props.
 */
const DrugsDiseases: React.FC<FilterTabNewsProps> = () => {
  const theme = useTheme() as Theme;
  const headerHeight = useCustomHeaderHeight();
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();
  const {tabs} = useSelector((state: RootState) => state.homeTabs);
  const flatListRef = useRef<FlatList>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const tabContainerWidth = getByScreenSize(wdp(90), wdp(40));
  const tabWidth = tabContainerWidth / 4.1;

  // Animated values
  const scrollX = useSharedValue(0);
  const slidePosition = useSharedValue(0);
  const screenWidth = Dimensions.get('window').width;

  // Handle back press from detail screen
  const handleBackPress = () => {
    if (search) {
      setSearch('');
      return true;
    }
    return false;
  };

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandlerSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandlerSubscription.remove();
      };
    }
  }, [handleBackPress]);

  // Animation for the tab indicator
  const animatedSliderStyle = useAnimatedStyle(() => {
    return {
      transform: [
        {
          translateX: withSpring(
            activeIndex === 0 ? -tabWidth : tabWidth,
            SPRING_CONFIG,
          ),
        },
      ],
    };
  });

  // Scroll handler for the FlatList
  const scrollHandler = useAnimatedScrollHandler({
    onScroll: event => {
      scrollX.value = event.contentOffset.x;
      const position = event.contentOffset.x / screenWidth;
      slidePosition.value = position > 0.5 ? 1 : 0;
    },
  });

  // Update active index from scroll position
  useDerivedValue(() => {
    const position = Math.round(scrollX.value / screenWidth);
    if (position !== activeIndex) {
      runOnJS(setActiveIndex)(position);
    }
  });

  const onViewCallBack = React.useCallback(
    ({viewableItems}: {viewableItems: Array<ViewToken>}) => {
      if (
        viewableItems &&
        viewableItems.length > 0 &&
        viewableItems[0] !== undefined
      ) {
        setActiveIndex(viewableItems[0].index || 0);
        slidePosition.value = viewableItems[0].index || 0;
      }
    },
    [],
  );

  const selectedTabs = useMemo<Record<TabKey, HomeTabItemType>>(
    () => pickTabsByKey(tabs, [TabKey.Drugs, TabKey.Diseases]),
    [tabs],
  );

  const renderTabItem: ListRenderItem<TabKey> = React.useCallback(
    ({item}) => {
      return (
        <ItemContainer key={item} screenWidth={wdp(100)}>
          <DrugsDiseasesTab
            tabKey={item}
            topComponent={_renderTopTaps}
            onRecordingStateChange={setIsRecording}
          />
        </ItemContainer>
      );
    },
    [selectedTabs, wdp, hdp],
  );

  const scrollTab = React.useCallback(
    (index: number) => {
      if (index === activeIndex) {
        return;
      }

      slidePosition.value = index;

      if (flatListRef.current) {
        flatListRef.current.scrollToIndex({
          index,
          animated: true,
        });
      }
    },
    [activeIndex, slidePosition],
  );

  const _renderTopTaps = React.useCallback(
    (isVisible?: boolean) => {
      return (
        <TabContainer isHidden={!isVisible}>
          <LinearGradient
            colors={[
              theme.drugsDiseases.gradientTopLeft,
              theme.drugsDiseases.gradientMidTop,
              theme.drugsDiseases.gradientCenter,
              theme.drugsDiseases.gradientBottomRight,
            ]}
            start={{x: 0, y: 0}}
            end={{x: 1, y: 1}}
            style={{
              flex: 1,
              width: '100%',
              paddingTop: isVisible ? headerHeight + hdp(2) : theme.text.s55,
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Title semiBold>{tr('drugsAndDiseases.tabTitle')}</Title>
            <TabContentContainer tabContainerWidth={tabContainerWidth}>
              <AnimatedSlider style={animatedSliderStyle} />
              {LIST.map((type, idx) => (
                <GenericButton
                  isSmall={getByScreenSize(false, true)}
                  key={idx}
                  label={
                    selectedTabs[type]?.tab_name ||
                    tr(`drugsAndDiseases.${type}`)
                  }
                  labelStyle={{
                    color: theme.text.light,
                    fontFamily: getFontFamily().bold,
                  }}
                  style={{
                    width: '49.5%',
                    borderRadius: 100,
                    overflow: 'hidden',
                    backgroundColor: 'transparent',
                  }}
                  radius={100}
                  onPress={() => {
                    setSearch('');
                    scrollTab(idx);
                  }}
                />
              ))}
            </TabContentContainer>
            <SearchInputContainer
              tabContainerWidth={tabContainerWidth}
              tabMarginVertical={theme.text.s55}>
              <SearchInput
                minHeight={getByScreenSize(55, 60)}
                placeholder={tr('drugsAndDiseases.searchPlaceholder')}
                placeholderTextColor={theme.text.light}
                leftIconName={'search'}
                leftIconColor={theme.text.light}
                showLeftIcon
                value={search}
                inputContainerStyle={{
                  backgroundColor: 'transparent',
                  borderWidth: 1,
                  borderColor: theme.text.light,
                  borderRadius: 100,
                }}
                onChangeText={text => setSearch(text)}
              />
            </SearchInputContainer>
          </LinearGradient>
        </TabContainer>
      );
    },
    [
      activeIndex,
      headerHeight,
      hdp,
      selectedTabs,
      theme,
      animatedSliderStyle,
      tabContainerWidth,
      scrollTab,
      search,
    ],
  );

  return (
    <MainLayout
      backHeader
      menuHeader
      noPadding
      keyboardAvoidScrollView
      title={tr('drugsAndDiseases.title')}
      noPaddingHeader>
      <Container>
        {_renderTopTaps(true)}
        {search ? (
          <Animated.View
            entering={FadeInDown.springify()}
            exiting={FadeOutDown.springify()}>
            <DrugsDiseasesSearch
              topComponent={_renderTopTaps}
              tabKey={LIST[activeIndex]}
              search={search}
            />
          </Animated.View>
        ) : (
          <Animated.FlatList
            data={LIST}
            ref={flatListRef}
            //@ts-ignore
            renderItem={renderTabItem}
            keyExtractor={item => item + ''}
            horizontal
            pagingEnabled
            contentContainerStyle={{
              flexGrow: 1,
            }}
            style={{
              zIndex: isRecording ? 3 : 1,
            }}
            initialScrollIndex={0}
            showsHorizontalScrollIndicator={false}
            viewabilityConfig={{viewAreaCoveragePercentThreshold: 50}}
            onViewableItemsChanged={onViewCallBack}
            getItemLayout={(data, index) => ({
              length: wdp(100),
              offset: wdp(100) * index,
              index,
            })}
            onScroll={scrollHandler}
            // performance
            initialNumToRender={1}
            maxToRenderPerBatch={2}
            removeClippedSubviews
            windowSize={2}
            scrollEventThrottle={16}
          />
        )}
      </Container>
    </MainLayout>
  );
};

export default DrugsDiseases;
